import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Modal, Space, Table, TableColumnsType, Typography } from 'antd'
import dayjs from 'dayjs'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import { FEATURE } from 'constants/feature'
import { TokenizeStatusEnum } from 'constants/pto'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { formatDateLocal } from 'utils/format-time'
import { logError, logInfo } from 'utils/logger'

const { Text } = Typography

interface RewardPoolItem {
  _id: string
  tokenizeId: string
  ownerAddress: string
  status: number
  claimEnabled: number
  contractAddress: string
  createdAt: string
  updatedAt: string
  poolId: string
  rarity: number
  rewardAmount: number
  availableReward: number
  asset: string
  activationDate: string
  tokenize: {
    fullName: string
    contractAddress: string
    thumbnail: string
    status: TokenizeStatusEnum
  }
  ticketName: string
}

const fetchRewardsPool = async (searchParams: Record<string, any>) => {
  const response = await AppAdmin.getListRewardsPool(searchParams)
  logInfo('Existing rewards', response.data.data)

  if (response.data?.data) {
    return {
      rewards: response.data.data.results as RewardPoolItem[],
      total: response.data.data.totalResults,
    }
  }
  return { rewards: [], total: 0 }
}

export const PTOExistingRewardsTable = () => {
  const queryClient = useQueryClient()
  const { searchParams, initPagination, onChangePagination } = useSearchParams()
  const { canCRUD } = usePermission()
  const canCRUDRewards = canCRUD(FEATURE.PTO_REWARDS)

  const { data, isLoading } = useQuery({
    queryKey: ['rewardsPool', searchParams],
    queryFn: () => fetchRewardsPool(searchParams),
    gcTime: 30000,
  })

  const existingRewards = data?.rewards ?? []
  const total = data?.total ?? 0

  const columns: TableColumnsType<RewardPoolItem> = [
    {
      title: 'PTO',
      dataIndex: ['tokenize', 'fullName'],
      key: 'pto',
      render: (value) => <Text style={{ maxWidth: 100 }}>{value || '-'}</Text>,
    },
    {
      title: 'Ticket Program',
      dataIndex: 'ticketName',
      key: 'ticketProgram',
    },
    {
      title: 'Reward Amount',
      dataIndex: 'rewardAmount',
      key: 'rewardAmount',
      render: (value) => value || 0,
    },
    {
      title: 'Available Rewards',
      dataIndex: 'availableReward',
      key: 'availableReward',
      render: (value) => value ?? 0,
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (value) => (value ? <LinkAddress address={value} type="address" max={4} /> : '-'),
    },
    {
      title: 'Asset',
      dataIndex: 'asset',
      key: 'asset',
      align: 'center',
      render: (asset) => <span className="existing-rewards__asset">{asset}</span>,
    },
    {
      title: 'Activation Date',
      dataIndex: 'activationDate',
      key: 'activationDate',
      render: (date) => formatDateLocal(date),
    },
    {
      title: 'Status',
      dataIndex: ['tokenize', 'status'],
      key: 'status',
      render: (status) => {
        const isActive = status === 2
        return (
          <span
            className={`existing-rewards__status ${
              isActive ? 'status-success' : 'status-completed'
            }`}
          >
            {isActive ? 'active' : 'inactive'}
          </span>
        )
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'created',
      render: (date) => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
    },
    ...(canCRUDRewards
      ? [
          {
            title: 'Actions',
            dataIndex: '',
            key: 'action',
            align: 'right' as const,
            render: (_: unknown, record: RewardPoolItem) => (
              <Space size="middle">
                <Button
                  color="danger"
                  disabled={record.tokenize.status !== 2 || record.availableReward !== 0}
                  onClick={() => {
                    Modal.confirm({
                      type: 'warning',
                      title: (
                        <Flex vertical>
                          <div>Delete Confirm</div>
                          <div style={{ fontSize: 12, color: '#6a7282' }}>
                            This action cannot be undone
                          </div>
                        </Flex>
                      ),

                      centered: true,
                      content:
                        'Are you sure you want to delete this reward? All associated data will be permanently removed from the system.',
                      footer: (_, { CancelBtn }) => (
                        <>
                          <CancelBtn />
                          <Button
                            color="danger"
                            className="button-delete"
                            onClick={() => deleteReward(record._id, record.rarity)}
                          >
                            Delete
                          </Button>
                        </>
                      ),
                    })
                  }}
                  className="button-delete"
                >
                  Delete
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ]

  const deleteReward = async (rewardPool: string, rarityId: number) => {
    try {
      await AppAdmin.deleteRewardPool({
        rarityId,
        rewardPool,
      })
      Modal.destroyAll()
      queryClient.invalidateQueries({ queryKey: ['rewardsPool'] })
    } catch (error) {
      logError('Failed to delete reward:', error)
    }
  }

  return (
    <Table
      columns={columns}
      loading={isLoading}
      dataSource={existingRewards}
      rowKey={(record) => `${record._id}-${record.rarity}`}
      pagination={initPagination(total)}
      onChange={onChangePagination}
      scroll={{ x: 1000 }}
      bordered
    />
  )
}
